/**
 * ProgressHeader - Indicador de progreso y reset
 *
 * Muestra:
 * - Progreso total (X de Y items)
 * - Alerta visual si faltan items criticos
 * - Boton de reset
 */

import { useMemo } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useChecklistStore } from '@/store';
import type { Checklist, ChecklistProgress } from '@/core/types';
import { cn } from '@/core/utils';
import { Button, ProgressBar } from '@/ui/primitives';

interface ProgressHeaderProps {
  checklist: Checklist;
}

export function ProgressHeader({ checklist }: ProgressHeaderProps) {
  const resetChecklist = useChecklistStore((state) => state.resetChecklist);

  // Calcular progreso localmente para evitar re-renders infinitos
  const progress: ChecklistProgress = useMemo(() => {
    const total = checklist.items.length;
    const checked = checklist.items.filter((i) => i.isChecked).length;
    const criticalItems = checklist.items.filter((i) => i.priority === 'critical');
    const criticalChecked = criticalItems.filter((i) => i.isChecked).length;

    return {
      total,
      checked,
      percentage: total > 0 ? Math.round((checked / total) * 100) : 0,
      criticalTotal: criticalItems.length,
      criticalChecked,
      allCriticalDone:
        criticalItems.length === 0 || criticalChecked === criticalItems.length,
    };
  }, [checklist.items]);

  const handleReset = () => {
    if (window.confirm('¿Desmarcar todos los items?')) {
      resetChecklist(checklist.id);
    }
  };

  const isComplete = progress.percentage === 100;
  const hasCriticalPending = !progress.allCriticalDone;

  return (
    <div className="bg-slate-900 rounded-xl p-4 space-y-3">
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{checklist.emoji}</span>
          <div>
            <h2 className="text-lg font-semibold text-slate-100">
              {checklist.name}
            </h2>
            {checklist.description && (
              <p className="text-sm text-slate-400">{checklist.description}</p>
            )}
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleReset}
          disabled={progress.checked === 0}
          aria-label="Desmarcar todo"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-slate-400">
            {progress.checked} de {progress.total} items
          </span>
          <span
            className={cn(
              'font-medium',
              isComplete ? 'text-emerald-400' : 'text-slate-300'
            )}
          >
            {progress.percentage}%
          </span>
        </div>
        <ProgressBar
          value={progress.percentage}
          layer={isComplete ? 'growth' : 'maintenance'}
        />
      </div>

      {/* Critical Items Warning */}
      {hasCriticalPending && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-2 bg-rose-900/30 border border-rose-800/50 rounded-lg"
        >
          <AlertTriangle className="w-4 h-4 text-rose-400" />
          <span className="text-sm text-rose-300">
            Faltan {progress.criticalTotal - progress.criticalChecked} items
            criticos
          </span>
        </motion.div>
      )}

      {/* Complete State */}
      {isComplete && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-2 p-2 bg-emerald-900/30 border border-emerald-800/50 rounded-lg"
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span className="text-sm text-emerald-300">Listo para salir</span>
        </motion.div>
      )}
    </div>
  );
}
